"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.SelectMenu = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _core = require("@emotion/core");
var _react = require("react");
var _i18n = require("../../../../i18n/i18n");
var _useTheme = require("../../../../theme/utils/useTheme");
var _flyout = require("../../../flyout");
var _getMenuAnimation = require("../../../menu/styled/getMenuAnimation");
var _menuTheme = require("../../../menu/styled/menuTheme");
var _utils = require("../../utils");
var _CreatableOption = require("../CreatableOption");
var _SelectAllOption = require("../multiselect/SelectAllOption");
var _NoResultsMessage = require("../shared/NoResultsMessage");
var _LoadingOption = require("./LoadingOption");
var _VariableSizeListStyled = require("./VariableSizeListStyled");
var _VirtualizedOption = require("./VirtualizedOption");
var _VirtualizedSeparatorStyled = require("./VirtualizedSeparatorStyled");
/** @jsxRuntime classic */
/** @jsx jsx */

const SelectMenu = _ref => {
  let {
    extraMenuProps,
    flyoutProps,
    formatOption,
    getItemProps,
    getMenuProps,
    highlightedIndex,
    isLoading,
    isOpen,
    isOptionDisabled,
    menuWidth,
    maxMenuHeight,
    maxOptionsUntilVirtualization,
    optionHeight: providedOptionHeight,
    options,
    targetWidth,
    allSelected,
    enableSelectAll,
    inputValue,
    selectedItems,
    selectedOption,
    getOptionKey,
    className
  } = _ref;
  const {
    i18n
  } = (0, _i18n.useI18n)();
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const selectAllOptionHeight = _menuTheme.menuTheme.optionHeight.normal(themeMode);
  const optionHeight = providedOptionHeight || _menuTheme.menuTheme.optionHeight.normal(themeMode);
  const separatorHeight = _menuTheme.menuTheme.separatorHeight(themeMode);
  const showAsListbox = isOpen && options.length > 0;
  const shouldVirtualize = options.length > maxOptionsUntilVirtualization;
  const listRef = (0, _react.useRef)(null);
  const totalSelectedItems = selectedItems.length;
  //menu options without the select all, separator markers and creatable marker
  const regularOptions = options.filter(option => option.value !== undefined && option.label !== undefined);
  const totalMenuItems = regularOptions.length;
  const {
    // Pull this off - we'll reattach our own later.
    "aria-labelledby": _unused,
    ...restMenuProps
  } = getMenuProps({
    "aria-label": showAsListbox ? i18n._( /*i18n*/"listbox contents") : undefined,
    id: flyoutProps.id,
    // We have to turn off the listbox role when it doesn't have any children. This is
    // from the spec - a listbox MUST have children of the role "option" | "group".
    role: showAsListbox ? "listbox" : "none",
    ...extraMenuProps
  }, {
    // The Flyout doesn't attach itself until a tick later. Trust us downshift, we're good :)
    suppressRefError: true
  });
  const isOptionSelected = (0, _react.useCallback)(option => {
    return selectedOption === option || selectedItems.includes(option);
  }, [selectedItems, selectedOption]);
  const getRowSize = index => {
    if ((0, _utils.isSelectAllMarkerOption)(options[index])) {
      return selectAllOptionHeight + separatorHeight;
    } else if ((0, _utils.isSelectV2OptionSeparator)(options[index])) {
      return separatorHeight;
    }
    return optionHeight;
  };
  const getRenderedOption = (0, _react.useCallback)((option, index, highlightedIndex, extraProps) => {
    const isSelectAllOption = (0, _utils.isSelectAllMarkerOption)(option);
    const optionProps = {
      "data-highlighted": highlightedIndex === index ? "true" : "false",
      "aria-disabled": isOptionDisabled?.(option) ?? false
    };
    if (enableSelectAll && isSelectAllOption) {
      //destructuring to remove the role and aria-selected from the select all option to avoid screen reader reading selected/not-selected for the select all option
      const {
        role: _unusedRole,
        "aria-selected": _unusedAriaSelected,
        ...propsWithoutHeight
      } = extraProps;
      delete propsWithoutHeight.style?.height;
      return (0, _core.jsx)(_react.Fragment, {
        key: _utils.selectAllMarkerOption.value
      }, (0, _core.jsx)(_SelectAllOption.SelectAllOption, (0, _extends2.default)({
        totalSelectedItems: totalSelectedItems,
        totalOptions: totalMenuItems,
        checkedState: selectedItems.length === 0 ? "unchecked" : allSelected ? "checked" : "indeterminate"
      }, propsWithoutHeight, optionProps)), (0, _core.jsx)(_VirtualizedSeparatorStyled.VirtualizedSeparatorStyled, {
        shouldVirtualize: shouldVirtualize,
        optionHeight: selectAllOptionHeight
      }));
    } else if ((0, _utils.isCreatableMarkerOption)(option)) {
      return (0, _core.jsx)(_CreatableOption.CreatableOption, (0, _extends2.default)({
        inputValue: inputValue,
        optionHeight: optionHeight
      }, extraProps, {
        key: _utils.creatableMarkerOption.value,
        "aria-selected": "false"
      }, optionProps));
    } else if ((0, _utils.isSelectV2OptionSeparator)(option)) {
      return (0, _core.jsx)(_VirtualizedSeparatorStyled.VirtualizedSeparatorStyled, {
        shouldVirtualize: shouldVirtualize,
        optionHeight: separatorHeight,
        top: extraProps.style?.top,
        key: `${_utils.selectV2OptionSeparator.value}-${index}`
      });
    } else {
      return formatOption(option, index, highlightedIndex, {
        ...extraProps,
        ...optionProps
      }, isOptionSelected(option), {
        inputValue,
        isDisabled: isOptionDisabled?.(option) ?? false
      });
    }
  }, [allSelected, enableSelectAll, formatOption, inputValue, isOptionSelected, isOptionDisabled, optionHeight, selectAllOptionHeight, selectedItems.length, separatorHeight, shouldVirtualize, totalSelectedItems, totalMenuItems]);
  const itemData = (0, _react.useMemo)(() => ({
    getItemProps,
    getRenderedOption,
    highlightedIndex,
    options,
    getOptionKey,
    isOptionSelected,
    metadata: {
      inputValue
    }
  }), [getItemProps, getOptionKey, getRenderedOption, highlightedIndex, isOptionSelected, options, inputValue]);
  const itemKey = (0, _react.useCallback)(index => {
    const option = options[index];
    if ((0, _utils.isSelectV2OptionSeparator)(option)) return `${option.value}-${index}`;
    if ((0, _utils.isSelectAllMarkerOption)(option) || (0, _utils.isCreatableMarkerOption)(option)) return option.value;
    if (isLoading && index === options.length) return "loading-indicator";
    return getOptionKey(option);
  }, [getOptionKey, options, isLoading]);
  (0, _react.useEffect)(() => {
    listRef.current?.resetAfterIndex(0);
  }, [options]);
  (0, _react.useEffect)(() => {
    if (highlightedIndex >= 0) listRef.current?.scrollToItem(highlightedIndex);
  }, [highlightedIndex]);

  // increase item count by one if loading to allow for the loading indicator to be rendered
  const itemCount = isLoading ? options.length + 1 : options.length;
  const menu = isOpen ? (0, _core.jsx)(_react.Fragment, null, shouldVirtualize ? (0, _core.jsx)(_VariableSizeListStyled.VariableSizeListStyled, {
    height: maxMenuHeight,
    width: "100%",
    itemCount: itemCount,
    itemSize: getRowSize,
    itemKey: itemKey,
    ref: listRef,
    itemData: itemData
  }, _VirtualizedOption.VirtualizedOption) : (0, _core.jsx)(_react.Fragment, null, options.map((option, index) => {
    const isSelected = isOptionSelected(option);
    return getRenderedOption(option, index, highlightedIndex, {
      ...getItemProps({
        item: option,
        index,
        onClick: _utils.stopPropagationHandler,
        key: getOptionKey(option)
      }),
      // Override this for wai-aria 1.2 support. Downshift assumes that
      // selection follows focus and the last focused item in the dropdown
      // will be selected. This is not the case for us, so we need to
      // attach this attribute ourselves, otherwise screen readers (esp.
      // VoiceOver) will assume all items are selected.
      "aria-selected": isSelected ? "true" : "false"
    }, isSelected, {
      inputValue,
      isDisabled: isOptionDisabled?.(option) ?? false
    });
  }), isLoading && (0, _core.jsx)(_LoadingOption.LoadingOption, null)), !isLoading && (options.length === 0 || options.length === 1 && (0, _utils.isSelectAllMarkerOption)(options[0])) && (0, _core.jsx)(_NoResultsMessage.NoResultsMessage, {
    height: optionHeight,
    themeMode: themeMode
  })) : null;

  // when menuWidth options were introduced, this became
  // necessary in order to not break the existing placement API
  const getMenuPlacement = placement => {
    switch (placement) {
      case "bottom":
        return "bottom-start";
      case "top":
        return "top-start";
      default:
        return "auto";
    }
  };
  return (0, _core.jsx)(_flyout.Flyout, (0, _extends2.default)({}, flyoutProps, restMenuProps, {
    className: className,
    placement: getMenuPlacement(flyoutProps?.placement),
    style: {
      ...restMenuProps.style,
      width: menuWidth === "match-control" ? `${targetWidth}px` : `unset`,
      minWidth: menuWidth === "auto-grow" ? `${targetWidth}px` : `unset`,
      maxWidth: menuWidth === "auto-shrink" ? `${targetWidth}px` : `unset`,
      opacity: isOpen ? 1 : 0
    },
    css: isOpen ? (0, _core.css)`
                          padding: 4px 0;
                          overflow-y: auto;
                          overflow-x: hidden;
                          background-color: ${_menuTheme.menuTheme.backgroundColor.rest(themeMode)};
                          box-shadow: ${_menuTheme.menuTheme.boxShadow(themeMode)};
                          border-radius: ${_menuTheme.menuTheme.borderRadius(themeMode)};
                          max-height: ${maxMenuHeight}px;
                          box-sizing: border-box;
                          outline: none;
                          transition: opacity ${_menuTheme.menuTheme.motion.animation(themeMode)};
                          &[data-popper-placement="top-start"] {
                              animation: ${(0, _getMenuAnimation.getMenuAnimation)("top")} ${_menuTheme.menuTheme.motion.animation(themeMode)};
                          }
                          &[data-popper-placement="bottom-start"] {
                              animation: ${(0, _getMenuAnimation.getMenuAnimation)("bottom")} ${_menuTheme.menuTheme.motion.animation(themeMode)};
                          }

                          ${shouldVirtualize ? `
                              padding: 0;
                              ` : `
                              `}
                      ` : undefined
  }), menu);
};
exports.SelectMenu = SelectMenu;
//# sourceMappingURL=SelectMenu.js.map